/**
 * What is live on a task's forms, per language (spec P-4.8), from the two fingerprints the
 * sidecar keeps for each published form (§10 P-10.27, §9.1 I-11).
 *
 *   shape changed (a question, an option, a kind, what is required)  → QUESTIONS_CHANGED: the
 *     form is no longer offered, and the task asks those questions in the chat until she updates.
 *   only the words or the page layout changed                        → WORDS_CHANGED: the form
 *     keeps being sent; her newest wording is simply not on it yet.
 *   both current                                                      → LIVE.
 *   nothing recorded for that language                                → NOT_PUBLISHED.
 *
 * The TASK'S version never decides it: an edit to a step the form does not carry changes
 * neither fingerprint, and the row stays Live. Pure, so the tab and D3's arrange page read one
 * answer.
 */
export const FORM_STATE = Object.freeze({
  NOT_PUBLISHED: 'not_published',
  LIVE: 'live',
  WORDS_CHANGED: 'words_changed',
  QUESTIONS_CHANGED: 'questions_changed',
});

export const FORM_LANGS = Object.freeze(['en', 'ar']);

/** Nothing published in either language: exactly the row as it was before P-4.8. */
export const NOTHING_PUBLISHED = Object.freeze({
  en: Object.freeze({ state: FORM_STATE.NOT_PUBLISHED, flowId: null }),
  ar: Object.freeze({ state: FORM_STATE.NOT_PUBLISHED, flowId: null }),
});

const stateOf = row => {
  if (!row) return FORM_STATE.NOT_PUBLISHED;
  if (!row.shapeCurrent) return FORM_STATE.QUESTIONS_CHANGED;
  if (!row.textCurrent) return FORM_STATE.WORDS_CHANGED;
  return FORM_STATE.LIVE;
};

/**
 * @param publishedForms `flowsApi.list`'s `publishedForms`: an array, or null (could not check).
 * @param taskId the task's numeric id, as the tab lists it.
 * @returns `{ en: { state, flowId }, ar: { state, flowId } }`, or null when it could not check.
 */
export const formStanding = (publishedForms, taskId) => {
  if (!Array.isArray(publishedForms)) return null;
  const mine = publishedForms.filter(
    row => row.taskId !== null && String(row.taskId) === String(taskId)
  );
  return Object.fromEntries(
    FORM_LANGS.map(lang => {
      const row = mine.find(one => one.lang === lang) ?? null;
      return [lang, { state: stateOf(row), flowId: row?.flowId ?? null }];
    })
  );
};

/** True when neither language has anything recorded. */
export const nothingPublished = standing =>
  standing !== null &&
  FORM_LANGS.every(lang => standing[lang].state === FORM_STATE.NOT_PUBLISHED);

/** A form that is out of date in its questions or its words. */
export const isStale = state =>
  state === FORM_STATE.WORDS_CHANGED || state === FORM_STATE.QUESTIONS_CHANGED;
